import { Box, Grid, Hidden, Stack, Typography } from '@mui/material'
import { useLoginInfo } from 'app/_hooks'
import { default as Link } from 'next/link'
import { FC, useCallback, useLayoutEffect, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'
import { LoadingBackdrop } from '~/components/LoadingBackdrop'
import {
  playListVideoActionUiTypeState,
  targetActionPlayListVideoInfoState,
} from '~/features/play_list/store'
import { ShareModal } from '~/features/video/components/ShareModal'
import { VideoActionButton } from '~/features/video/components/VideoActionButton'
import { VideoInfo } from '~/features/video/components/VideoInfo'
import { useVideoLike } from '~/hooks/useVideoLike'
import { formatDurationWithUnit } from '~/libs/format'
import * as GA from '~/libs/ga'
import { Colors } from '~/libs/mui/theme'
import { useVideoDetail } from '../hooks/useVideoDetail'
import VideoUnavailable from './VideoUnavailable'

const VideoDetailInfo: FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation()
  const loginInfo = useLoginInfo()
  const [openShareModal, setOpenShareModal] = useState(false)
  const { data, unavailableReason, isTrailer } = useVideoDetail(id)
  const { currentUserHasLike, likeNum, onClickLike } = useVideoLike(id)
  const video = data?.originalVideo
  const setPlayListVideoInfo = useSetRecoilState(targetActionPlayListVideoInfoState)
  const setUiType = useSetRecoilState(playListVideoActionUiTypeState)

  const showAlert = useCallback(
    (isLike: boolean) => {
      switch (loginInfo.status) {
        case 'notSignedIn':
          alert(t(isLike ? 'login_to_like' : 'login_to_add_on_playlist'))
          break
        case 'noProfile':
          alert(t('please_create_profile'))
          break
        case 'conflict':
          alert(t('incorrect_login_method'))
          break
        case 'signedIn':
          switch (unavailableReason) {
            case 'REQUIRE_AUTH':
              alert(t('not_logged_in'))
              break
            case 'ALREADY_WITHDRAWAL':
              alert(t('already_canceled'))
              break
            case 'REQUIRE_RESUBSCRIPTION':
              alert(t('already_canceled'))
              break
            case 'REQUIRE_SUBSCRIPTION':
              alert(t(isLike ? 'login_to_like' : 'login_to_add_on_playlist'))
              break
            case 'INVALID_CHARGE':
              alert(t('invalid_payment_information'))
            default:
              break
          }
        default:
          break
      }
    },
    [t, loginInfo, unavailableReason],
  )

  const onClickAddPlayList = useCallback(
    (videoId: number) => {
      setUiType('select')
      setPlayListVideoInfo({ videoId })
    },
    [setUiType, setPlayListVideoInfo],
  )

  const [windowWidth, setWindowWidth] = useState(0)

  useLayoutEffect(() => {
    function updateSize() {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener('resize', updateSize)
    updateSize()

    return () => window.removeEventListener('resize', updateSize)
  }, [])

  if (isTrailer === undefined) {
    return <LoadingBackdrop />
  }

  return (
    <Box
      sx={{
        // NOTE: 動画プレイヤーのアスペクト比 16:9, 余白 16px
        paddingTop: { xs: `${(windowWidth * 9) / 16 + 16}px`, sm: 2 },
      }}
    >
      <Stack spacing={2}>
        {isTrailer && (
          <Hidden smUp>
            <VideoUnavailable unavailableReason={unavailableReason} />
          </Hidden>
        )}

        <Box display='flex' alignItems='center' gap={2}>
          <Typography color={Colors.yellow} fontSize={{ xs: '18px', sm: '28px' }}>
            {video?.title}
          </Typography>
          {isTrailer && (
            <Typography
              color={Colors.yellow}
              fontSize={{ xs: '12px', sm: '16px' }}
              borderColor={Colors.yellow}
              border='solid 1px'
              borderRadius='4px'
              paddingX={1}
            >
              予告
            </Typography>
          )}
        </Box>

        <Grid container rowGap={2}>
          <Grid item xs={12} sm={6}>
            <VideoInfo
              id={id}
              creator={{
                name: video?.creator?.name ?? '',
                avatarUrl: video?.creator?.avatar ?? '',
              }}
              duration={formatDurationWithUnit(
                video?.duration?.minutes ?? undefined,
                video?.duration?.minuteUnitName ?? undefined,
                video?.duration?.seconds ?? undefined,
                video?.duration?.secondUnitName ?? undefined,
              )}
              description={video?.description ?? ''}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display='flex' justifyContent={{ xs: 'center', sm: 'end' }}>
              <VideoActionButton
                iconType='add_playlist'
                text={t('add')}
                onClick={() => {
                  if (isTrailer) {
                    showAlert(false)
                    return
                  }
                  if (video?.id) {
                    onClickAddPlayList(parseInt(video.id))
                  }
                }}
              />
              <VideoActionButton
                iconType='like'
                text={`${t('like')} ${likeNum ?? 0}`}
                highlight={currentUserHasLike == true}
                onClick={() => {
                  if (isTrailer) {
                    showAlert(true)
                    return
                  }
                  onClickLike()
                }}
              />
              <VideoActionButton
                iconType='share'
                text={t('share')}
                onClick={() => setOpenShareModal(true)}
              />
              {video?.commentaryUrl && (
                <VideoActionButton
                  iconType='comment'
                  text={t('note')}
                  onClick={() => {
                    GA.sendEvent({
                      event: 'explanation',
                      screen: 'movie_page',
                      category: 'icon',
                      action: 'movie_page_explanation',
                      label: `${video?.id}`,
                    })
                    window.open(video.commentaryUrl ?? '', '_blank')
                  }}
                />
              )}
              {video?.filmarksUrl && (
                <VideoActionButton
                  iconType='filmarks'
                  text='Filmarks'
                  onClick={() => {
                    window.open(video.filmarksUrl ?? '', '_blank')
                  }}
                />
              )}
            </Box>
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              {video?.categories?.map((category) => (
                <Link
                  key={category.id}
                  href={`/search/categories/${category.id}/videos`}
                  onClick={() => {
                    GA.sendEvent({
                      event: 'click',
                      screen: 'movie_page',
                      category: 'text',
                      action: 'movie_page_tag',
                      label: `${video?.id}`,
                      value: category.name ?? '',
                    })
                  }}
                >
                  <Typography
                    component='span'
                    sx={{
                      backgroundColor: Colors.color1A1A18,
                      padding: '4px 8px',
                      margin: '4px',
                      borderRadius: '4px',
                      display: 'inline-block',
                      color: Colors.color5A5957,
                      fontSize: 13,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: isMobile ? undefined : Colors.yellow },
                    }}
                  >
                    {category.name}
                  </Typography>
                </Link>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Stack>
      <ShareModal
        open={openShareModal}
        onClose={() => setOpenShareModal(false)}
        previewVideoId={video?.id ?? ''}
        title={video?.title ?? ''}
        description={video?.description ?? ''}
      />
    </Box>
  )
}

export default VideoDetailInfo
